import { Component, OnInit, OnDestroy, Input, inject, effect, ChangeDetectorRef } from '@angular/core';
import { Router } from "@angular/router";
import { CommonModule } from '@angular/common';
import { EntityList } from '../../objects/entity-list';
import { FormsModule } from "@angular/forms";
import { Subject, combineLatest, filter, takeUntil } from 'rxjs';
import { UsersService } from '../../service/entity-services/users.service';

import { MatProgressBar } from '@angular/material/progress-bar';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';

import { Contract } from '../../objects/contract';
import { MyWorkFilterService, MyWorkFilter } from '../../service/my-work-filter.service';

@Component({
    selector: 'app-contract-list',
    templateUrl: './contract-list.component.html',
    styleUrls: ['./contract-list.component.css'],
    standalone: true,
    imports: [
      CommonModule,
      FormsModule,
      MatProgressBar,
      MatButtonToggleModule,
      MatTableModule,
      MatSortModule,
      MatIconModule,
      MatChipsModule,
      MatTooltipModule
    ]})
export class ContractListComponent extends EntityList implements OnInit, OnDestroy {

  public filterService = inject(MyWorkFilterService);
  private usersServiceLocal = inject(UsersService);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();

  public allContracts: Contract[] = [];
  public filteredContracts: Contract[] = [];

  waitingForFilterResults = false;
  isLoading = true;

  // Expose MyWorkFilter enum to template
  MyWorkFilter = MyWorkFilter;

  @Input() showContracts: boolean = false;

  // Computed signal for the entity type display name
  entityTypeName = 'Contracts';

  displayedColumns: string[] = [
    'status',
    'nameOfContract',
    'directVendor',
    'primaryOwner',
    'status_field',
    'expirationDate'
  ];

  constructor(public router: Router){
    super();

    // Set default filter: "I am the Owner" - do this before setting up the effect
    this.filterService.addFilter(MyWorkFilter.Owner);

    // Set up effect to watch for filter changes (signal-based)
    // This re-applies filters when the user toggles filter buttons
    effect(() => {
      // Read signals to establish dependencies
      const activeFilters = this.filterService.activeFilters();
      const userEmail = this.filterService.currentUserEmail();

      // Apply filtering whenever filters or user email change
      if (userEmail) {
        this.applyFilters();
        this.cdr.detectChanges();
      }
    });
  }

  async ngOnInit() {
    // Use combineLatest to ensure we have BOTH contracts AND user email
    // before applying filters. This fixes the race condition where contracts
    // arrive before the email is resolved (or vice versa).
    combineLatest([
      this.contractService.visibleContracts$,
      this.usersServiceLocal.currentUserEmail$.pipe(filter(email => !!email))
    ]).pipe(
      takeUntil(this.destroy$)
    ).subscribe(([contracts, userEmail]) => {
      this.allContracts = contracts;
      this.isLoading = false;
      this.applyFilters();
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Applies current filter service filters to the contracts list
   */
  private applyFilters(): void {
    this.filteredContracts = this.filterService.filterContracts(this.allContracts);
  }

  /**
   * Handler for filter button toggle changes
   * Material toggle group with multiple="true" returns an array of selected values
   */
  userChangedFilter(event: any): void {
    const selectedValues: string[] = event.value || [];

    // Clear all filters first
    this.filterService.clearFilters();

    // Add each selected filter
    selectedValues.forEach(value => {
      switch (value) {
        case 'Owner':
          this.filterService.addFilter(MyWorkFilter.Owner);
          break;
        case 'ExpiringSoon':
          this.filterService.addFilter(MyWorkFilter.ExpiringSoon);
          break;
        case 'Expired':
          this.filterService.addFilter(MyWorkFilter.Expired);
          break;
        case 'NextReviewDateSoon':
          this.filterService.addFilter(MyWorkFilter.NextReviewDateSoon);
          break;
      }
    });
    // The effect will automatically re-filter when the signal changes
  }

  /**
   * Checks if a filter is currently active
   */
  isFilterActive(filter: MyWorkFilter): boolean {
    return this.filterService.isFilterActive(filter);
  }

  onRowClicked(contract: Contract): void {
    this.router.navigate(['/securehome/contract', contract.contractNum]);
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
    } catch {
      return dateString;
    }
  }

  extractOwnerName(owner: any): string {
    if (!owner) return '';
    if (typeof owner === 'string') return owner;
    if (owner.userData) {
      const { given_name, family_name } = owner.userData;
      if (given_name && family_name) {
        return `${family_name}, ${given_name}`;
      }
    }
    if (owner.friendlyName) return owner.friendlyName;
    return owner.email || owner.emailAddress || owner.userId || '';
  }

  extractVendorName(vendor: any): string {
    if (!vendor) return '';
    if (typeof vendor === 'string') return vendor;
    if (vendor.vendorData?.name) return vendor.vendorData.name;
    if (vendor.name) return vendor.name;
    return '';
  }

  isExpired(contract: Contract): boolean {
    if (!contract.expirationDate) return false;
    return new Date(contract.expirationDate) < new Date();
  }

  isExpiringSoon(contract: Contract): boolean {
    if (!contract.expirationDate) return false;
    const expDate = new Date(contract.expirationDate);
    const now = new Date();
    const daysUntil = (expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return daysUntil >= 0 && daysUntil <= 180;
  }

  sortData(sort: Sort): void {
    if (!sort.active || sort.direction === '') {
      this.applyFilters();
      return;
    }

    this.filteredContracts = [...this.filteredContracts].sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'nameOfContract': return this.compare(a.nameOfContract, b.nameOfContract, isAsc);
        case 'directVendor': return this.compare(this.extractVendorName(a.directVendor), this.extractVendorName(b.directVendor), isAsc);
        case 'primaryOwner': return this.compare(this.extractOwnerName((a as any).primaryOwner), this.extractOwnerName((b as any).primaryOwner), isAsc);
        case 'status_field': return this.compare(a.status, b.status, isAsc);
        case 'expirationDate': return this.compare(a.expirationDate, b.expirationDate, isAsc);
        default: return 0;
      }
    });
  }

  private compare(a: any, b: any, isAsc: boolean): number {
    if (!a && !b) return 0;
    if (!a) return isAsc ? -1 : 1;
    if (!b) return isAsc ? 1 : -1;
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

}
